import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Building2, Users, Car, Bell, Mail, HardDrive, Blocks, ScrollText } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader, Card, Kpi } from "../components/core/Layout";
import { fmtDateTime } from "../lib/utils";
import { PlatformResetCard } from "../components/platform/PlatformResetCard";
import { useAuth } from "../lib/auth";

interface Platform {
  tenants: { total: number; active: number };
  users: { super_admin: number; client_admin: number; inactive: number };
  vehicles: { active: number; archived: number };
  open_alerts: number;
  email: { mode: string; sender?: string | null; sent: number; failed: number; logged: number; last_at?: string | null };
  storage: { files: number; provider: string };
  modules: { key: string; label: string; tenants: number }[];
  audit_entries: number;
}

const Row = ({ k, v, testId }: { k: string; v: React.ReactNode; testId?: string }) => <div className="flex items-center justify-between py-2 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800" data-testid={testId}>{v}</span></div>;

export default function PlatformSettingsPage() {
  const { user } = useAuth();
  const { data } = useQuery<Platform>({ queryKey: ["platform-summary"], queryFn: async () => (await api.get("/dashboard/platform")).data });
  if (!data) return <p className="text-sm text-slate-500">Loading platform summary…</p>;
  const live = data.email.mode === "resend";
  return (
    <div data-testid="platform-settings-page">
      <PageHeader eyebrow="Settings" title="Platform" subtitle="ENFLEET platform health at a glance — clients, users, integrations and module catalogue." />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Clients" value={data.tenants.active} icon={Building2} tone="brand" testId="platform-kpi-tenants" hint={`${data.tenants.total} total · ${data.tenants.total - data.tenants.active} inactive`} />
        <Kpi label="Users" value={data.users.super_admin + data.users.client_admin} icon={Users} testId="platform-kpi-users" hint={`${data.users.super_admin} super · ${data.users.client_admin} client · ${data.users.inactive} disabled`} delay={1} />
        <Kpi label="Registered vehicles" value={data.vehicles.active} icon={Car} testId="platform-kpi-vehicles" hint={`${data.vehicles.archived} archived`} delay={2} />
        <Kpi label="Open alerts" value={data.open_alerts} icon={Bell} tone={data.open_alerts ? "danger" : "ok"} testId="platform-kpi-alerts" delay={3} />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Email delivery" icon={Mail} testId="platform-email-card" className="rise rise-1">
          <div className="divide-y divide-slate-100">
            <Row k="Provider" v={<span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${live ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{live ? "Resend · Live" : "Mock · log only"}</span>} testId="platform-email-mode" />
            <Row k="Sender" v={data.email.sender || "—"} testId="platform-email-sender" />
            <Row k="Sent / failed" v={`${data.email.sent} / ${data.email.failed}`} />
            <Row k="Logged (mock)" v={data.email.logged} />
            <Row k="Last message" v={fmtDateTime(data.email.last_at)} />
          </div>
        </Card>
        <Card title="Storage & audit" icon={HardDrive} testId="platform-storage-card" className="rise rise-2">
          <div className="divide-y divide-slate-100">
            <Row k="Object storage" v={data.storage.provider} />
            <Row k="Stored files" v={data.storage.files} testId="platform-files" />
            <Row k="Audit entries" v={<span className="inline-flex items-center gap-1"><ScrollText className="h-3.5 w-3.5 text-slate-400" />{data.audit_entries.toLocaleString()}</span>} testId="platform-audit-entries" />
          </div>
        </Card>
        <Card title="Module catalogue" icon={Blocks} testId="platform-modules-card" className="rise rise-3" actions={<Link to="/bo/settings/users/roles" className="text-xs font-semibold text-[color:var(--brand)] hover:underline">Access matrix →</Link>}>
          <ul className="divide-y divide-slate-100">
            {data.modules.map((m) => (
              <li key={m.key} className="flex items-center justify-between py-2 text-sm" data-testid={`platform-module-${m.key}`}>
                <span className="text-slate-800">{m.label}</span>
                <span className="text-xs text-slate-500">{m.tenants}/{data.tenants.total} clients</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {user?.role !== "finance" && <div className="mt-6"><PlatformResetCard /></div>}
    </div>
  );
}
